/**
 * Alert Component
 *
 * Displays important messages and notifications with different severity levels.
 * Supports five variants: default, destructive, success, warning, and info.
 *
 * @example
 * // Basic alert
 * <Alert variant="info">
 *   This is an informational message.
 * </Alert>
 *
 * @example
 * // Alert with title
 * <Alert variant="success">
 *   <AlertTitle>Success!</AlertTitle>
 *   Your changes have been saved successfully.
 * </Alert>
 *
 * @example
 * // Dismissible alert
 * <Alert variant="warning" onClose={() => console.log('dismissed')}>
 *   <AlertTitle>Warning</AlertTitle>
 *   Please review your changes before proceeding.
 * </Alert>
 *
 * @example
 * // Alert with custom icon
 * <Alert variant="info" icon={<CustomIcon />}>
 *   Custom notification message.
 * </Alert>
 */

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  AlertCircle,
  XCircle,
  CheckCircle,
  AlertTriangle,
  Info,
  X
} from 'lucide-react'

import { cn } from '@/lib/utils'

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground border-border',
        destructive:
          'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900',
        success:
          'bg-green-50 text-green-800 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900',
        warning:
          'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900',
        info:
          'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

/**
 * Props for the Alert component
 */
export interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  /**
   * Optional callback when alert is dismissed
   */
  onClose?: () => void
  /**
   * Optional custom icon to display
   */
  icon?: React.ReactNode
}

/**
 * Props for the AlertTitle component
 */
export interface AlertTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}

/**
 * Props for the AlertDescription component
 */
export interface AlertDescriptionProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Map variants to their default icons
 */
const variantIcons = {
  default: Info,
  destructive: XCircle,
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info,
}

/**
 * Alert component for displaying notifications and messages
 */
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'default', onClose, icon, children, ...props }, ref) => {
    // Determine which icon to show
    const IconComponent = variantIcons[variant || 'default']
    const showIcon = icon !== null
    const iconToRender = icon !== undefined ? icon : <IconComponent className="h-4 w-4" aria-hidden="true" />

    // Determine ARIA role based on variant
    const role = variant === 'destructive' || variant === 'warning' ? 'alert' : 'status'

    return (
      <div
        ref={ref}
        role={role}
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        <div className="flex gap-3">
          {showIcon && (
            <div className="flex-shrink-0 mt-0.5">
              {iconToRender}
            </div>
          )}
          <div className="flex-1">
            {children}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 ml-3 inline-flex rounded-md p-1 hover:bg-black/10 dark:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent focus:ring-current transition-colors"
              aria-label="Dismiss alert"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    )
  }
)
Alert.displayName = 'Alert'

/**
 * AlertTitle component for displaying a title within an Alert
 */
const AlertTitle = React.forwardRef<HTMLHeadingElement, AlertTitleProps>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('font-medium leading-none tracking-tight mb-1', className)}
      {...props}
    />
  )
)
AlertTitle.displayName = 'AlertTitle'

/**
 * AlertDescription component for displaying description text within an Alert
 */
const AlertDescription = React.forwardRef<HTMLDivElement, AlertDescriptionProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('text-sm [&_p]:leading-relaxed', className)}
      {...props}
    />
  )
)
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription, alertVariants }
